import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { getChat, startChat, finishChat, sendChatMessage } from './aiClientData';
import SupervisorDashboard from './SupervisorDashboard';

const MAX_MESSAGE_LENGTH = 2000;

export default function AIChat({ instance, token, onRunningChange }) {
    const [chat, setChat] = useState(null);
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');
    const [pending, setPending] = useState(false);
    const [remaining, setRemaining] = useState(0);
    const [confirmEnd, setConfirmEnd] = useState(false);
    const retry = useRef(null);
    const revision = useRef(0);
    const busy = useRef(false);
    const anchor = useRef(null);
    const alive = useRef(true);
    const transcriptEnd = useRef(null);
    const expiredRefresh = useRef(null);
    const sync = useCallback(incoming => {
        if (!alive.current) return;
        // 서버가 아직 active라도 시간이 지났으면 화면에서는 종료로 처리한다.
        const value = incoming.status === 'active' && incoming.expiresAt - incoming.serverNow <= 0
            ? { ...incoming, status: 'completed', endedAt: incoming.expiresAt, endReason: 'time_limit' } : incoming;
        setChat(value);
        const ms = value.status === 'active' ? Math.max(0, value.expiresAt - value.serverNow) : 0;
        anchor.current = { ms, at: performance.now() };
        setRemaining(Math.ceil(ms / 1000));
        onRunningChange?.(value.status === 'active');
    }, [onRunningChange]);
    const refresh = useCallback(async () => {
        const current = revision.current;
        try { const value = await getChat(instance.id, token); if (current === revision.current && !busy.current) sync(value); }
        catch (failure) { if (alive.current) setError(failure.message); }
    }, [instance.id, token, sync]);
    useEffect(() => {
        alive.current = true;
        onRunningChange?.(true);
        refresh();
        return () => { alive.current = false; clearTimeout(expiredRefresh.current); onRunningChange?.(false); };
    }, [refresh, onRunningChange]);
    useEffect(() => {
        if (chat?.status !== 'active') return undefined;
        const timer = setInterval(() => {
            if (anchor.current) setRemaining(Math.max(0, Math.ceil((anchor.current.ms - (performance.now() - anchor.current.at)) / 1000)));
        }, 1000);
        const poll = setInterval(() => { if (!busy.current) refresh(); }, 10000);
        return () => { clearInterval(timer); clearInterval(poll); };
    }, [chat?.status, refresh]);
    useEffect(() => {
        // 프론트 타이머가 끝나면 서버 응답을 기다리지 않고 바로 입력을 막고 종료 화면으로 바꾼다.
        if (chat?.status !== 'active' || remaining > 0) return;
        setChat(previous => previous?.status === 'active' ? { ...previous, status: 'completed', endedAt: previous.expiresAt, endReason: 'time_limit' } : previous);
        setConfirmEnd(false);
        onRunningChange?.(false);
        // 서버의 종료 기록(endedAt 등)은 잠시 뒤 한 번만 다시 불러온다.
        clearTimeout(expiredRefresh.current);
        expiredRefresh.current = setTimeout(refresh, 2000);
    }, [chat?.status, remaining, onRunningChange, refresh]);
    useEffect(() => {
        transcriptEnd.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }, [chat?.messages.length]);
    async function action(operation) {
        if (busy.current) return;
        revision.current += 1;
        busy.current = true; setPending(true); setError('');
        try { const value = await operation(); sync(value); return value; }
        catch (failure) {
            if (alive.current) setError(failure.message);
            if (failure.code === 'CHAT_ENDED') { try { sync(await getChat(instance.id, token)); } catch { /* retry via reload */ } }
            return null;
        } finally { busy.current = false; if (alive.current) setPending(false); }
    }
    async function send(event) {
        event.preventDefault();
        const text = draft.trim();
        if (!text || text.length > MAX_MESSAGE_LENGTH || chat?.status !== 'active' || remaining <= 0 || busy.current) return;
        if (!retry.current || retry.current.text !== text) retry.current = { turnId: crypto.randomUUID(), text };
        const value = await action(() => sendChatMessage(instance.id, token, retry.current));
        if (value && alive.current) { setDraft(''); retry.current = null; }
    }
    const minutes = Math.floor(remaining / 60), seconds = remaining % 60;
    const showDashboard = chat?.status === 'active' || chat?.status === 'completed';
    return <section className="ai-chat ai-interview-shell" aria-label="AI 내담자 면담">
        <div className="ai-chat-header"><div><span className="ai-kicker">COUNSELING PRACTICE</span><h2>{instance.client.name} 님과의 면담</h2></div>{chat?.status === 'active' && <div className={`ai-chat-timer ${remaining <= 120 ? 'ending' : ''}`} role="timer" aria-label="남은 면담 시간">{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}</div>}</div>
        {error && <div role="alert" className="ai-alert">{error} <button type="button" disabled={pending} onClick={() => { setError(''); refresh(); }}>대화 다시 불러오기</button></div>}
        <div className={`ai-interview-workspace${showDashboard ? '' : ' ai-interview-before-start'}`}><section className="ai-chat-panel" aria-label="내담자 채팅창">
        <h3 className="ai-chat-panel-label">내담자 채팅창</h3>
        {!chat && <p role="status">저장된 면담을 확인하고 있습니다.</p>}
        {chat?.status === 'prepared' && <div className="ai-chat-start"><p>준비된 내담자와 {instance.durationMinutes}분 동안 대화합니다. 시작하면 첫 발화와 함께 시간이 흐릅니다.</p><p className="ai-small">면담은 하루 최대 3회까지 시작할 수 있습니다. 시작하면 새로고침하거나 화면을 닫아도 면담 시간은 계속 흐릅니다.</p><button type="button" disabled={pending} className="ai-primary" onClick={() => action(() => startChat(instance.id, token))}>{pending ? '면담을 준비하는 중…' : `${instance.durationMinutes}분 면담 시작`}</button></div>}
        {chat && chat.status !== 'prepared' && <>
            <div className="ai-transcript" role="log" aria-label="면담 대화 기록" aria-live="polite">{chat.messages.map(message => <div key={message.id} className={`ai-message ${message.role === 'user' ? 'counselor' : 'client'}`}><span>{message.role === 'user' ? '나 · 상담자' : instance.client.name}</span><p>{message.content}</p></div>)}{pending && <p role="status" className="ai-small">응답을 처리하고 있습니다…</p>}<div ref={transcriptEnd} /></div>
            {chat.status !== 'completed' && <>
                <form onSubmit={send}><label htmlFor="ai-chat-draft">상담자로서 답변하기</label><textarea id="ai-chat-draft" value={draft} maxLength={MAX_MESSAGE_LENGTH} rows={3} disabled={pending || remaining === 0} onChange={e => setDraft(e.target.value.slice(0, MAX_MESSAGE_LENGTH))} aria-describedby="ai-chat-draft-count" placeholder="내담자의 말에 응답해보세요." />
                    <div className="ai-chat-controls"><span id="ai-chat-draft-count" className="ai-small">{draft.length.toLocaleString()} / 2,000자 · {draft.length >= MAX_MESSAGE_LENGTH ? <strong role="status" className="ai-chat-limit">메시지는 2,000자까지 입력할 수 있습니다.</strong> : '실패한 문장은 수정하거나 다시 전송할 수 있습니다.'}</span><button className="ai-primary" type="submit" disabled={pending || remaining === 0 || !draft.trim()}>{pending ? '전송 중…' : '메시지 보내기'}</button></div></form>
                <div className="ai-chat-end">{confirmEnd ? <><span>지금 종료하면 이 면담을 다시 시작할 수 없습니다.</span><button type="button" disabled={pending} onClick={() => action(() => finishChat(instance.id, token))}>면담 종료 확인</button><button type="button" disabled={pending} onClick={() => setConfirmEnd(false)}>계속 대화하기</button></> : <button type="button" disabled={pending} onClick={() => setConfirmEnd(true)}>면담 일찍 마치기</button>}</div>
            </>}
        </>}
    </section>{showDashboard && <SupervisorDashboard chat={chat} />}</div>
    {chat?.status === 'completed' && <div className="ai-chat-completed"><h3>면담이 종료되었습니다.</h3><p>{chat.endReason === 'time_limit' ? '설정한 면담 시간이 지났습니다.' : '면담을 직접 종료했습니다.'} 대화 기록은 저장되어 다시 확인할 수 있습니다.</p><p className="ai-small">다음 단계에서 이 면담을 바탕으로 사례개념화를 작성하고 항목별 AI 피드백을 받습니다.</p><Link className="ai-next-step" to="/case-conceptualization">다음 단계 · 사례 개념화 →</Link></div>}
    </section>;
}
AIChat.propTypes = { instance: PropTypes.object.isRequired, token: PropTypes.string.isRequired, onRunningChange: PropTypes.func };
